import { useEffect } from "react";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  author?: string;
  image?: string;
  url?: string;
  type?: "website" | "article" | "product";
}

export function SEO({
  title = "Brenneman Systems - Business Automation & Integration",
  description = "Transform your business with cutting-edge AI solutions. Custom development, seamless integration, and expert consulting services.",
  keywords = "business automation, AI integration, custom development, workflow optimization, API integration, AI consulting, business transformation, automation solutions",
  author = "Brenneman Systems",
  image = "https://brenneman-systems.com/og-image.png",
  url = "https://brenneman-systems.com",
  type = "website"
}: SEOProps) {
  useEffect(() => {
    // Update document title
    document.title = title;

    // Update meta tags
    const updateMetaTag = (property: string, content: string, isProperty = false) => {
      const attribute = isProperty ? "property" : "name";
      let element = document.querySelector(`meta[${attribute}="${property}"]`);

      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, property);
        document.head.appendChild(element);
      }

      element.setAttribute("content", content);
    };

    // Basic meta tags
    updateMetaTag("description", description);
    updateMetaTag("keywords", keywords);
    updateMetaTag("author", author);

    // Open Graph meta tags
    updateMetaTag("og:title", title, true);
    updateMetaTag("og:description", description, true);
    updateMetaTag("og:type", type, true);
    updateMetaTag("og:url", url, true);
    updateMetaTag("og:image", image, true);
    updateMetaTag("og:site_name", "Brenneman Systems", true);

    // Twitter Card meta tags
    updateMetaTag("twitter:card", "summary_large_image");
    updateMetaTag("twitter:title", title);
    updateMetaTag("twitter:description", description);
    updateMetaTag("twitter:image", image);
    updateMetaTag("twitter:site", "@brennemansystems");

    // Add structured data (JSON-LD)
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Brenneman Systems",
      "alternateName": "Brenneman Systems LLC",
      "url": "https://brenneman-systems.com",
      "logo": "https://brenneman-systems.com/logo.png",
      "description": "Business automation and AI integration solutions provider",
      "sameAs": [
        "https://www.linkedin.com/company/brenneman-systems",
        "https://github.com/brenneman-systems",
        "https://twitter.com/brennemansystems"
      ],
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "sales",
        "email": "contact@brenneman-systems.com",
        "availableLanguage": ["English"]
      },
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "US"
      },
      "offers": [
        {
          "@type": "Offer",
          "name": "Custom AI Development",
          "description": "Build tailored AI solutions that solve your specific business challenges"
        },
        {
          "@type": "Offer",
          "name": "Workflow Automation",
          "description": "Automate repetitive tasks and optimize your business processes"
        },
        {
          "@type": "Offer",
          "name": "API & System Integration",
          "description": "Connect your existing tools and create seamless data flows"
        },
        {
          "@type": "Offer",
          "name": "AI Strategy Consulting",
          "description": "Expert guidance on implementing AI in your business"
        }
      ],
      "areaServed": {
        "@type": "Country",
        "name": "United States"
      },
      "priceRange": "$$$$"
    };

    // Add or update structured data script
    let scriptElement = document.querySelector('script[type="application/ld+json"]');
    if (!scriptElement) {
      scriptElement = document.createElement("script");
      scriptElement.setAttribute("type", "application/ld+json");
      document.head.appendChild(scriptElement);
    }
    scriptElement.textContent = JSON.stringify(structuredData);

    // Add canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute("href", url);

    // Add language declaration
    document.documentElement.lang = "en";

  }, [title, description, keywords, author, image, url, type]);

  return null;
}
